<template>
  <div class="kpi-card">
    <div class="kpi-label-row">
      <span class="kpi-label">{{ label }}</span>
      <el-tooltip
        v-if="tooltip"
        :content="tooltip"
        placement="top"
        effect="dark"
      >
        <i class="el-icon-info kpi-info-icon"></i>
      </el-tooltip>
    </div>
    <div class="kpi-value-row">
      <span :class="['kpi-value', { 'kpi-value--hidden': hidden }]">
        <template v-if="hidden">****</template>
        <template v-else>
          <template v-if="prefix === '$'">$</template>
          {{ formattedValue }}
          <template v-if="prefix === '%'">%</template>
        </template>
      </span>
    </div>
    <div
      class="kpi-change"
      v-if="!hidden && change !== null && change !== undefined"
    >
      <span :class="['change-badge', direction]">
        <i
          :class="
            direction === 'up'
              ? 'el-icon-top'
              : direction === 'down'
              ? 'el-icon-bottom'
              : 'el-icon-minus'
          "
        ></i>
        {{ change }}%
      </span>
      <span class="change-label">
        vs
        <template v-if="hasPrevious">
          <strong class="change-previous">{{ formattedPrevious }}</strong>
          anterior
        </template>
        <template v-else>período anterior</template>
      </span>
    </div>
    <!-- Skeleton loading -->
    <div v-if="loading" class="kpi-skeleton">
      <div class="skeleton-line w60"></div>
      <div class="skeleton-line w80"></div>
      <div class="skeleton-line w40"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "KpiCard",
  props: {
    label: { type: String, default: "" },
    value: { type: Number, default: 0 },
    previous: { type: Number, default: null },
    change: { type: Number, default: null },
    direction: { type: String, default: "neutral" },
    prefix: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    tooltip: { type: String, default: "" }
  },
  data() {
    return {
      hidden: localStorage.getItem("kpi-hide-values") === "true"
    };
  },
  mounted() {
    this.$root.$on("privacy-changed", this._onPrivacyChanged);
  },
  beforeDestroy() {
    this.$root.$off("privacy-changed", this._onPrivacyChanged);
  },
  methods: {
    _onPrivacyChanged(val) {
      this.hidden = val;
    }
  },
  computed: {
    formattedValue() {
      if (this.prefix === "$") {
        return new Intl.NumberFormat("es-EC", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(this.value);
      }
      return this.value;
    },
    hasPrevious() {
      return (
        this.previous !== null &&
        this.previous !== undefined &&
        this.previous > 0
      );
    },
    formattedPrevious() {
      if (this.previous === null || this.previous === undefined) return "";
      if (this.prefix === "$") {
        const num = new Intl.NumberFormat("es-EC", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(this.previous);
        return `$${num}`;
      }
      if (this.prefix === "%") return `${this.previous}%`;
      return this.previous;
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3fdbbf;

.kpi-card {
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 10px;
  padding: 16px 18px;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
}
.kpi-label-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
}
.kpi-label {
  font-size: 12px;
  font-weight: 600;
  color: #909399;
  line-height: 1.3;
}
.kpi-info-icon {
  font-size: 12px;
  color: #c0c4cc;
  cursor: help;
  &:hover {
    color: #909399;
  }
}
.kpi-value-row {
  display: flex;
  align-items: baseline;
}
.kpi-value {
  font-size: 30px;
  font-weight: 500;
  color: $primary;
  line-height: 1;
  &--hidden {
    letter-spacing: 3px;
    color: #c0c4cc;
  }
}
.kpi-change {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.change-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  &.up {
    background: #e4fff4;
    color: #1d9e75;
  }
  &.down {
    background: #fff0f0;
    color: #e24b4a;
  }
  &.neutral {
    background: #fff8e6;
    color: #ba7517;
  }
}
.change-label {
  font-size: 12px;
  color: #909399;
}
.change-previous {
  color: #31355c;
  font-weight: 600;
}

// Skeleton
.kpi-skeleton {
  position: absolute;
  inset: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 16px 18px;
  gap: 8px;
}
.skeleton-line {
  height: 12px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  &.w40 {
    width: 40%;
  }
  &.w60 {
    width: 60%;
  }
  &.w80 {
    width: 80%;
    height: 20px;
  }
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
